import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { Autocomplete, Button, Dialog, DialogContent, DialogTitle, FormControlLabel, IconButton, Radio, Stack, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { Formik } from 'formik';
import { FormikProps } from 'formik/dist/types';
import { isEmpty } from 'lodash';
import React, { ReactElement, SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import toastOptions from '../../helpers/toastOptions';
import useBooleanKeys from '../../hooks/useBooleanKeys';
import { deliverItem, deliverItemHome } from '../../redux/slices/assignments/assignmentsSlice';
import { getSitesByIdFetching, getSitesOptionsById, listSite } from '../../redux/slices/sites/sitesSlice';
import { getToolById } from '../../redux/slices/tools/toolsSlice';
import { useDispatch } from '../../redux/store';
import { TextField } from '../../styled/inputs';
import { ConfirmDialog } from '../confirmDialog/ConfirmDialog';
import SiteForm from '../site/SiteForm';

interface IProp {
  onClose: (redirectURL?: string, id?: string) => void;
  open: boolean;
  itemId?: string;
  identifier?: string;
}

interface Values {
  job_site_id: string;
  home_site_id: string;
}

const schema = yup.object().shape({});

export const DeliverForm = ({ onClose, open, itemId, identifier }: IProp): ReactElement => {
  const dispatch = useDispatch();
  const formikRef = useRef<FormikProps<Values>>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const jobSitesOptions = useSelector(getSitesOptionsById({ siteOwner: 'customer', active: 'true' }));
  const homeSitesOptions = useSelector(getSitesOptionsById({ siteOwner: 'organization', active: 'true' }));
  const fetchingJobSites = useSelector(getSitesByIdFetching);
  const [modals, toggleModal] = useBooleanKeys({ showAddSite: false, showConfirm: false });
  const [sending, setSending] = useState(false);
  const [type, setType] = useState('jobSite');

  useEffect(() => {
    dispatch(listSite({ siteOwner: 'customer', active: 'true' }));
    dispatch(listSite({ siteOwner: 'organization', active: 'true' }));
  }, [dispatch]);

  const handleChangeAutocompleteJobSites = (event: SyntheticEvent<Element, Event>, value) => {
    formikRef.current?.setFieldValue('job_site_id', value?.id || '');
  };
  const handleChangeAutocompleteHomeSites = (event: SyntheticEvent<Element, Event>, value) => {
    formikRef.current?.setFieldValue('home_site_id', value?.id || '');
  };

  const handleClose = useCallback(
    (event: SyntheticEvent<HTMLButtonElement>, reason?: string) => {
      if (reason !== 'backdropClick') {
        onClose();
      }
    },
    [onClose],
  );

  const handleDeliver = async () => {
    if (itemId) {
      try {
        setSending(true);
        if (type === 'jobSite') {
          await dispatch(deliverItem({ itemId, deliveryTarget: { site_id: formikRef?.current?.values.job_site_id } })).unwrap();
        } else {
          await dispatch(deliverItemHome({ itemId, deliveryTarget: { site_id: formikRef?.current?.values.home_site_id } })).unwrap();
        }

        await dispatch(getToolById(itemId));
        const targetLabel =
          type === 'jobSite'
            ? jobSitesOptions.find((option) => option.id === formikRef?.current?.values.job_site_id)?.label
            : homeSitesOptions.find((option) => option.id === formikRef?.current?.values.home_site_id)?.label;
        toast.success(`${identifier} was delivered to ${targetLabel}`, toastOptions);
        toggleModal('showConfirm');
        onClose();
      } catch (error) {
        console.log(error); // eslint-disable-line
      } finally {
        setSending(false);
      }
    }
  };

  const selectedJobSite = jobSitesOptions.find((site) => site.id === formikRef?.current?.values.job_site_id);
  const selectedHomeSite = homeSitesOptions.find((site) => site.id === formikRef?.current?.values.home_site_id);

  const handleCloseSiteForm = useCallback(
    async (redirect?: string) => {
      if (redirect) {
        await dispatch(listSite({ siteOwner: 'customer' }));
        setTimeout(() => {
          formikRef.current?.setFieldValue('job_site_id', redirect);
        }, 500);
      }
      toggleModal('showAddSite');
    },
    [dispatch, toggleModal],
  );

  return (
    <React.Fragment>
      <Dialog open={open} fullWidth maxWidth="md" onClose={handleClose}>
        <Formik
          innerRef={formikRef}
          validationSchema={schema}
          onSubmit={() => {}}
          initialValues={{
            job_site_id: '',
            home_site_id: '',
          }}
        >
          {({ handleSubmit, values, touched, errors }) => {
            const jobValue = jobSitesOptions.find((site) => site.id === values.job_site_id);
            const homeValue = homeSitesOptions.find((site) => site.id === values.home_site_id);
            return (
              <form noValidate onSubmit={handleSubmit}>
                <DialogTitle>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="h3" component="div">
                      Deliver item virtually
                    </Typography>

                    <IconButton onClick={handleClose}>
                      <CloseOutlinedIcon />
                    </IconButton>
                  </Stack>
                </DialogTitle>
                <DialogContent ref={contentRef} sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 260px)' }}>
                  <FormControlLabel
                    control={<Radio name="type" value="jobSite" checked={type === 'jobSite'} onChange={() => setType('jobSite')} />}
                    label="Deliver to job site"
                  />

                  <Stack direction="row" spacing={4} alignItems="center">
                    <Box flex="auto">
                      <Autocomplete
                        options={jobSitesOptions}
                        value={jobValue ?? null}
                        loading={fetchingJobSites}
                        noOptionsText="No Job Sites"
                        disabled={type !== 'jobSite' || fetchingJobSites || isEmpty(jobSitesOptions)}
                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                        onChange={handleChangeAutocompleteJobSites}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            name="job_site_id"
                            label="Job site for delivery"
                            error={Boolean(touched.job_site_id && errors.job_site_id)}
                            fullWidth
                            helperText={touched.job_site_id && errors.job_site_id}
                            variant="outlined"
                            my={2}
                            disabled={type !== 'jobSite'}
                          />
                        )}
                        sx={{ ml: 7 }}
                      />
                    </Box>
                    <Box sx={{ width: 75 }}>
                      <Button color="warning" variant="contained" onClick={() => toggleModal('showAddSite')} disabled={type !== 'jobSite'}>
                        Create
                      </Button>
                    </Box>
                  </Stack>
                  <FormControlLabel
                    control={<Radio name="type" value="homeSite" checked={type === 'homeSite'} onChange={() => setType('homeSite')} />}
                    label="Return to home site"
                  />
                  <Stack direction="row" spacing={4} alignItems="center">
                    <Box flex="auto">
                      <Autocomplete
                        options={homeSitesOptions}
                        value={homeValue ?? null}
                        loading={fetchingJobSites}
                        noOptionsText="No Home Sites"
                        disabled={type !== 'homeSite' || fetchingJobSites || isEmpty(homeSitesOptions)}
                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                        onChange={handleChangeAutocompleteHomeSites}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            name="home_site_id"
                            label="Home site for delivery"
                            error={Boolean(touched.home_site_id && errors.home_site_id)}
                            fullWidth
                            helperText={touched.home_site_id && errors.home_site_id}
                            variant="outlined"
                            my={2}
                            disabled={type !== 'homeSite'}
                          />
                        )}
                        sx={{ ml: 7 }}
                      />
                    </Box>
                    <Box sx={{ width: 75 }} />
                  </Stack>
                  <Stack direction="row" justifyContent="flex-end" spacing={4} sx={{ mt: 4 }}>
                    <Box component="span">
                      <Button variant="text" onClick={handleClose}>
                        Cancel
                      </Button>
                    </Box>
                    <Box component="span">
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => toggleModal('showConfirm')}
                        disabled={!(values.job_site_id || values.home_site_id) || fetchingJobSites}
                      >
                        Deliver
                      </Button>
                    </Box>
                  </Stack>
                </DialogContent>
              </form>
            );
          }}
        </Formik>
        <ConfirmDialog
          open={modals.showConfirm}
          onClose={() => toggleModal('showConfirm')}
          title="Deliver"
          onConfirm={handleDeliver}
          confirmLabel="Deliver"
          sending={sending}
        >
          Are you sure you want to deliver {identifier} to {type === 'jobSite' ? selectedJobSite?.label : selectedHomeSite?.label}?
        </ConfirmDialog>
      </Dialog>
      <SiteForm open={modals.showAddSite} onClose={handleCloseSiteForm} redirectTemplate="{siteId}" />
    </React.Fragment>
  );
};
